import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, TrendingUp, MessageSquare, Database, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlanBilling() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState({
    total_clients: 0,
    total_conversations: 0,
    total_messages: 0,
    total_knowledge_entries: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to load usage:', error);
      toast.error('Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Plan & Billing</h1>
            <p className="text-gray-600">Manage your subscription and view usage</p>
          </div>
        </div>

        {/* Current Plan Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Current Plan
            </CardTitle>
            <CardDescription>Your active subscription</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div>
                  <h3 className="text-xl font-semibold text-blue-900">Free Plan</h3>
                  <p className="text-blue-600">Unlimited access to all features</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-900">$0</div>
                  <div className="text-sm text-blue-600">per month</div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">Plan Features:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Unlimited chatbots
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Unlimited conversations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Unlimited knowledge base entries
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Analytics dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Contact form integration
                  </li>
                </ul>
              </div>

              <Button variant="outline" disabled>
                Upgrade Plan (Coming Soon)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Usage Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Usage Statistics
            </CardTitle>
            <CardDescription>Your account activity overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Clients */}
              <Card className="border-2 border-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-800">
                    {usage.total_clients}
                  </div>
                  <div className="text-sm text-gray-600">Chatbots Created</div>
                </CardContent>
              </Card>

              {/* Total Conversations */}
              <Card className="border-2 border-green-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <MessageSquare className="w-8 h-8 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-800">
                    {usage.total_conversations}
                  </div>
                  <div className="text-sm text-gray-600">Total Conversations</div>
                </CardContent>
              </Card>

              {/* Total Messages */}
              <Card className="border-2 border-purple-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <MessageSquare className="w-8 h-8 text-purple-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-800">
                    {usage.total_messages}
                  </div>
                  <div className="text-sm text-gray-600">Messages Sent</div>
                </CardContent>
              </Card>

              {/* Knowledge Entries */}
              <Card className="border-2 border-orange-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Database className="w-8 h-8 text-orange-500" />
                  </div>
                  <div className="text-3xl font-bold text-gray-800">
                    {usage.total_knowledge_entries}
                  </div>
                  <div className="text-sm text-gray-600">Knowledge Entries</div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You're currently on the Free Plan with unlimited access to all features. 
                Paid plans with advanced features and priority support will be available soon.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Billing Information (Placeholder) */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Billing Information</CardTitle>
            <CardDescription>Payment method and billing history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No billing information on file</p>
              <p className="text-sm mt-2">Billing features will be available when paid plans launch</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
